import React, { useState, useEffect } from "react";
import axios from 'axios';

const TopUp = () => {
  const [showCopied, setShowCopied] = useState(false);
  const [hasAccount, setHasAccount] = useState(false);
  const [bvn, setBvn] = useState("");
  const [user, setUser] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const validateVirtualAccount = (account) => {
    if (!account) return false;
    if (!account.accountNumber || account.accountNumber.trim() === "") return false;
    if (!account.bankName || account.bankName.trim() === "") return false;
    return true;
  };

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        setError("Authentication required");
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/api/v1/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const userData = response.data;
      const isValidAccount = validateVirtualAccount(userData?.virtualAccount);
      setUser(userData);
      setHasAccount(isValidAccount);
      
      if (!isValidAccount && userData?.virtualAccount) {
        console.log('Invalid account data:', userData.virtualAccount);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setError("Failed to fetch user data. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (bvn.length !== 11) {
      setError("BVN must be 11 digits");
      return;
    }

    setIsGenerating(true);

    try {
      if (!user?.email || !user?.phone) {
        setError("User profile data is incomplete");
        return;
      }

      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_BASE_URL}/api/v1/generate-virtualaccount`,
        { 
          bvn, 
          email: user.email, 
          phone: user.phone
        },
        { 
          headers: { 
            Authorization: `Bearer ${token}`, 
            "Content-Type": "application/json" 
          } 
        }
      );

      await fetchUserData();
    } catch (error) {
      console.error("Error generating account:", error);
      setError(error.response?.data?.message || "Failed to generate virtual account. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const formatAmount = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return "0.00";
    return numAmount.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="text-center text-red-600">
            <p>Please log in to access your wallet.</p>
          </div>
        </div>
      </div>
    );
  }

  const accountDetails = [
    { label: "Bank Name", value: user?.virtualAccount?.bankName },
    { label: "Account Number", value: user?.virtualAccount?.accountNumber },
    { label: "Account Name", value: `${user?.fullname} flw` }
  ].filter(item => item.value && typeof item.value === 'string' && item.value.trim() !== '');

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-2xl font-semibold text-navy mb-6">Fund Your Wallet</h2>
        
        <div className="mb-8 p-6 bg-navy rounded-lg text-white">
          <p className="text-soft-white mb-2">Current Balance</p>
          <h3 className="text-3xl font-bold">₦{formatAmount(user?.virtualAccount?.accountAmount || 0)}</h3>
        </div>

        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex items-center">
                <span className="material-icons text-red-500 mr-2">error</span>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {hasAccount && accountDetails.length >= 2 ? (
            <div>
              <h3 className="text-lg font-medium text-navy mb-4">Your Virtual Account Details</h3>
              <div className="space-y-4 bg-soft-white p-6 rounded-lg">
                {accountDetails.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>
                      <p className="text-charcoal-gray text-sm">{item.label}</p>
                      <p className="text-navy font-medium">{item.value}</p>
                    </div>
                    <button 
                      onClick={() => copyToClipboard(item.value)}
                      className="text-emerald-green hover:text-emerald-600 transition-colors p-2"
                    >
                      <span className="material-icons">content_copy</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <div className="flex items-start">
                  <span className="material-icons text-blue-500 mr-3">info</span>
                  <div>
                    <h4 className="text-blue-800 font-medium mb-1">BVN Requirement</h4>
                    <p className="text-sm text-blue-700">
                      As mandated by the Central Bank of Nigeria (CBN), a Bank Verification Number (BVN) 
                      is required to create your virtual account for secure and verified transactions.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-charcoal-gray text-sm font-medium mb-2">
                    Enter your BVN:
                  </label>
                  <input
                    type="text"
                    value={bvn}
                    onChange={(e) => setBvn(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder="Enter 11-digit BVN"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-emerald-green focus:ring-1 focus:ring-emerald-green transition-colors"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Your BVN will be used only for account verification</p>
                </div>

                <button 
                  type="submit"
                  disabled={isGenerating || bvn.length !== 11}
                  className={`w-full py-3 rounded-lg transition-colors flex items-center justify-center gap-2
                    ${isGenerating || bvn.length !== 11 
                      ? 'bg-gray-300 cursor-not-allowed' 
                      : 'bg-emerald-green hover:bg-emerald-600 text-white'}`}
                >
                  {isGenerating ? (
                    <>
                      <span className="material-icons animate-spin">refresh</span>
                      Generating Account...
                    </>
                  ) : (
                    <>
                      <span className="material-icons">account_balance</span>
                      Generate Virtual Account
                    </>
                  )}
                </button>
              </form>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-charcoal-gray mb-2">Why do we need your BVN?</h4>
                <ul className="text-xs text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="material-icons text-emerald-green text-sm">check_circle</span>
                    <span>To comply with CBN regulations for virtual accounts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="material-icons text-emerald-green text-sm">check_circle</span>
                    <span>To ensure secure and verified transactions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="material-icons text-emerald-green text-sm">check_circle</span>
                    <span>To protect against fraudulent activities</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {showCopied && (
            <div className="fixed bottom-4 right-4 bg-emerald-green text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
              <span className="material-icons">check_circle</span>
              <span>Copied to clipboard!</span>
            </div>
          )}

          <div className="bg-soft-white rounded-lg p-6">
            <h3 className="text-lg font-medium text-navy mb-4">How to Fund Your Wallet</h3>
            <ol className="space-y-3 text-charcoal-gray">
              {[
                "Copy your virtual account details above",
                "Log in to your bank app or visit any bank branch",
                "Transfer any amount to the virtual account number",
                "Your wallet will be credited instantly"
              ].map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-green text-white flex items-center justify-center text-sm">
                    {index + 1}
                  </span>
                  <p>{step}</p>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-soft-white rounded-lg p-6">
            <h3 className="text-lg font-medium text-navy mb-4">Important Information</h3>
            <ul className="space-y-2">
              {[
                { icon: "info", text: "This account is uniquely tied to your profile" },
                { icon: "info", text: "Transfers are processed instantly" },
                { icon: "warning", text: "Do not share your virtual account details with others" }
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="material-icons text-emerald-green">{item.icon}</span>
                  <span className="text-charcoal-gray">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopUp;